
import { Component, Vue, Prop } from 'vue-property-decorator';
import draggable from 'vuedraggable';
import BprSectionValueEditor from '@/components/editor/SectionEditor/BprSectionEditor/BprSectionValueEditor.vue';
import BprForm from '@/components/editor/SectionEditor/BprSectionEditor/BprForm.vue';
import { textToOps, opsToText } from '@/utils/quill-delta.util';
import Op from 'quill-delta/dist/Op';
import { cloneDeep } from 'lodash';
import { mixins } from 'vue-class-component';
import { ViewModeMixin } from '@/utils/viewMode.mixin';
import { EditorCommonMixin } from '@/views/DocumentEditor/mixins/editor-common.mixin';
import { FullDocumentRevisionObject } from '@/jbi-shared/types/full-document-revision-object.types';
import { DirtyTagMap } from '@/store/modules/documents/types/documents.types';
import StagingBprForm from './StagingBprForm.vue';
import { PendingBprData } from '@/jbi-shared/types/cplus-bpr.types';
import SectionError from '@/components/editor/SectionEditor/SectionError.vue';

@Component({
  components: {
    draggable,
    BprSectionValueEditor,
    SectionTitleEditor: () =>
      import('@/components/editor/SectionEditor/SectionTitleEditor.vue'),
    SectionError,
    BprForm
  }
})
export default class StagingBprSectionEditor extends mixins(ViewModeMixin) {
  public drag = false;

  @Prop()
  public dirtyBprs!: PendingBprData[];
  @Prop()
  public dirtyCriterions!: FullDocumentRevisionObject['revision']['sections']['criterionSection'];
  @Prop()
  public existingBprs!: FullDocumentRevisionObject['revision']['sections']['bprSection'];
  @Prop()
  public newlyAddedBprs!: PendingBprData[];
  @Prop()
  public dirtyTagMaps!: DirtyTagMap[];
  @Prop()
  public bprSectionId!: number;

  get bprSectionTitle() {
    return textToOps('Best Practice Recommendations');
  }

  get bprNoticeMessages() {
    const noticeMessages = [];
    if (this.newlyAddedBprs.length > 0) {
      noticeMessages.push({
        message: 'New Best Practice Recommendation(s) found.'
      });
    }
    return noticeMessages;
  }

  get proxyBprs() {
    return this.dirtyBprs;
  }

  set proxyBprs(bprs: PendingBprData[]) {
    this.$emit('update:bprs', bprs);
  }

  public isHighlighted(bpr: PendingBprData) {
    return this.newlyAddedBprs.includes(bpr);
  }

  public handleDelete(
    subSection: FullDocumentRevisionObject['revision']['sections']['bprSection'][number],
    index: number
  ) {
    const { bprSubSectionId, tempId } = subSection;
    this.proxyBprs = this.dirtyBprs.filter((_, j) => j !== index);
    const updatedCriterions = this.dirtyCriterions.map((criterion) => {
      return {
        ...criterion,
        bprs: criterion.bprs.filter((bpr) =>
          bprSubSectionId
            ? bpr.bprSubSectionId !== bprSubSectionId
            : bpr.tempId !== tempId
        )
      };
    });
    this.$emit('update:criterions', updatedCriterions);
  }

  public openEditModal() {
    this.$buefy.modal.open({
      parent: this,
      component: StagingBprForm,
      hasModalCard: true,
      fullScreen: true,
      canCancel: true,
      trapFocus: true,
      props: {
        dirtyBprs: this.dirtyBprs,
        newlyAddedBprs: this.newlyAddedBprs,
        dirtyCriterions: this.dirtyCriterions,
        existingBprs: this.existingBprs,
        bprSectionId: this.bprSectionId
      },
      events: {
        'update:bprs': (e: PendingBprData[]) => this.$emit('update:bprs', e),
        'update:dirtyTagMaps': (e: DirtyTagMap[]) =>
          this.$emit('update:dirtyTagMaps', e)
      }
    });
  }
}
